import React from 'react'
import { useTranslation } from 'react-i18next'
import KibDivider from '@chewy/kib-divider-react'
import { NumberUtils } from '@pbt/pbt-ui-components'

import InvoiceType from '~/constants/InvoiceType'
import type { Estimate, InvoiceOrEstimate } from '~/types'

import { usePaymentDialog } from '../context/PaymentDialogProvider'

import styles from './InvoiceDetails.module.scss'

interface InvoiceDetailsProps {
  aggregatedPaymentAmount?: number
  invoice: InvoiceOrEstimate
  showAggregatedPayments?: boolean
}

const isEstimateFlow = (invoice: InvoiceOrEstimate): invoice is Estimate =>
  invoice.type === InvoiceType.ESTIMATE

export const InvoiceDetails = ({
  invoice,
  showAggregatedPayments = false,
  aggregatedPaymentAmount = 0,
}: InvoiceDetailsProps) => {
  const { t } = useTranslation(['Common', 'Dialogs'])
  const {
    subtotal = 0,
    additionalDiscount = 0,
    totalDiscount = 0,
    dueToPay = 0,
    totalTax = 0,
    requiredDeposit = 0,
  } = invoice
  const discounts = additionalDiscount + totalDiscount
  const invoiceTotal = subtotal - discounts + totalTax
  const remainingBalance = dueToPay
  const { isEstimate } = usePaymentDialog()

  const getRequiredDepositAmount = () => {
    if (isEstimateFlow(invoice)) {
      return invoice.requiredDepositAmountNoFee || dueToPay
    }
    return invoiceTotal
  }
  return (
    <div className={styles.invoiceDetailsContainer}>
      <h4 className={styles.sectionTitle}>
        {t('Dialogs:ADD_PAYMENT_DIALOG_V2.INVOICE_DETAILS')}
      </h4>
      <div className={styles.row}>
        <div className={styles.rowItem}>{t('Common:SUBTOTAL')}</div>
        <div className={styles.rowItem}>
          {NumberUtils.formatMoney(subtotal)}
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.rowItem}>{t('Common:DISCOUNTS')}</div>
        <div className={styles.rowItem}>
          {NumberUtils.formatMoney(discounts)}
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.rowItem}>{t('Common:TAX')}</div>
        <div className={styles.rowItem}>
          {NumberUtils.formatMoney(totalTax)}
        </div>
      </div>
      <KibDivider className={styles.invoiceDivider} color="alternate" />
      <div className={styles.row}>
        <div className={styles.rowItem}>
          {isEstimate ? t('Common:DEPOSIT_TOTAL') : t('Common:INVOICE_TOTAL')}
        </div>
        <div className={styles.rowItem}>
          {NumberUtils.formatMoney(getRequiredDepositAmount())}
        </div>
      </div>
      {isEstimate && (
        <div className={styles.row}>
          <div className={styles.rowItemSubtext}>
            {t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.ESTIMATE_DEPOSIT_LOW_PERCENTAGE',
              {
                percentage: NumberUtils.toPercentFormat(requiredDeposit),
              },
            )}
          </div>
        </div>
      )}
      {showAggregatedPayments && aggregatedPaymentAmount > 0 && (
        <div className={styles.row}>
          <div className={styles.rowItem}>
            {t('Dialogs:ADD_PAYMENT_DIALOG_V2.AGGREGATED_PAYMENTS')}
          </div>
          <div className={styles.rowItem}>
            ({NumberUtils.formatMoney(aggregatedPaymentAmount)})
          </div>
        </div>
      )}
      {!showAggregatedPayments &&
        invoice?.payments &&
        invoice?.payments?.length > 0 && (
          <>
            {invoice?.payments?.map((payment) => (
              <div className={styles.row} key={payment.id}>
                <div className={styles.rowItem}>
                  {isEstimate ? payment.paymentMethod : payment.method?.name}
                </div>
                <div className={styles.rowItem}>
                  ({NumberUtils.formatMoney(payment.amount)})
                </div>
              </div>
            ))}
            <div className={styles.row}>
              <div className={styles.rowItemBold}>
                {t('Dialogs:ADD_PAYMENT_DIALOG_V2.REMAINING_BALANCE')}
              </div>
              <div className={styles.rowItemBold}>
                ({NumberUtils.formatMoney(remainingBalance)})
              </div>
            </div>
          </>
        )}
      {showAggregatedPayments && aggregatedPaymentAmount > 0 && (
        <div className={styles.row}>
          <div className={styles.rowItemBold}>
            {t('Dialogs:ADD_PAYMENT_DIALOG_V2.REMAINING_BALANCE')}
          </div>
          <div className={styles.rowItemBold}>
            ({NumberUtils.formatMoney(remainingBalance)})
          </div>
        </div>
      )}
    </div>
  )
}
